import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Her zaman aktif
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Çerez onayı daha önce verilmiş mi kontrol et
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // 1.5 saniye sonra göster
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    const consentData = {
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 sm:p-6"
          data-testid="cookie-consent"
        >
          <div className="max-w-4xl mx-auto bg-[#121212] border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Main Banner */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Çerez Kullanımı
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                    Web sitemizde deneyiminizi iyileştirmek için çerezler kullanıyoruz. 
                    Siteyi kullanmaya devam ederek{' '}
                    <Link to="/gizlilik" className="text-violet-400 hover:underline">
                      Gizlilik Politikamızı
                    </Link>{' '}
                    ve çerez kullanımını kabul etmiş olursunuz.
                  </p>

                  {/* Detailed Options */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3 mb-4 pb-4 border-b border-zinc-800">
                          {/* Zorunlu Çerezler */}
                          <div className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-white">Zorunlu Çerezler</p>
                              <p className="text-xs text-zinc-500">Sitenin çalışması için gerekli</p>
                            </div>
                            <div className="w-10 h-6 bg-violet-500/20 rounded-full flex items-center justify-end px-1">
                              <div className="w-4 h-4 bg-violet-500 rounded-full flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            </div>
                          </div>

                          {/* Analitik Çerezler */}
                          <label className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg cursor-pointer">
                            <div>
                              <p className="text-sm font-medium text-white">Analitik Çerezler</p>
                              <p className="text-xs text-zinc-500">Site kullanımını analiz eder</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={preferences.analytics}
                              onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                              className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-violet-500 focus:ring-violet-500 focus:ring-offset-0 cursor-pointer"
                            />
                          </label>

                          {/* Pazarlama Çerezleri */}
                          <label className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg cursor-pointer">
                            <div>
                              <p className="text-sm font-medium text-white">Pazarlama Çerezleri</p>
                              <p className="text-xs text-zinc-500">Kişiselleştirilmiş reklamlar için</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={preferences.marketing}
                              onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                              className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-violet-500 focus:ring-violet-500 focus:ring-offset-0 cursor-pointer"
                            />
                          </label>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Buttons */}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid="cookie-accept-all"
                    >
                      Tümünü Kabul Et
                    </button>
                    {showDetails ? (
                      <button
                        onClick={handleAcceptSelected}
                        className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="cookie-accept-selected"
                      >
                        Seçilenleri Kabul Et
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowDetails(true)}
                        className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                        data-testid="cookie-settings"
                      >
                        <Settings className="w-4 h-4" />
                        Ayarlar
                      </button>
                    )}
                    <button
                      onClick={handleRejectAll}
                      className="px-5 py-2.5 text-zinc-400 hover:text-white text-sm font-medium transition-colors"
                      data-testid="cookie-reject"
                    >
                      Sadece Zorunlu
                    </button>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleRejectAll}
                  className="p-2 text-zinc-500 hover:text-white transition-colors"
                  aria-label="Kapat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
